// link.farmflix.tv → farmflix.tv path table (#883).
//
// A phone with the FarmFlix app opens https://link.farmflix.tv/<path> in the
// app. Anyone else (a computer, or a phone without the app) is sent by the
// link domain's CloudFront function to https://farmflix.tv/link/<path>, and
// the LinkResolver page uses this table to pick the matching website page.
//
// MIRROR of the app's deep-link table: FFCustomerApp/src/Utils/NavigationRef.ts
// (DEEP_LINK_ROUTES, plus its root and video/<id> special cases). A path
// added there must be added here too, or a web visitor falls through to a
// campaign with that slug, then to /app (/videos when logged in). That is
// safe, but the link loses its target.
//
// Patterns match the app's exactly, so the same link means the same thing in
// both places. `youtube/<id>` (#590) is ahead of the app: #562 adds it there.

export type LinkRoute = {
  pattern: RegExp;
  to: (param?: string) => string;
};

export const LINK_ROUTES: LinkRoute[] = [
  // App: empty path or "/" → Home; home and landing are named screens
  { pattern: /^$/, to: () => "/" },
  { pattern: /^home$/, to: () => "/" },
  // Website only (#1264): the get-the-app link. A phone with the app opens the
  // app, which has no screen of its own for this path; anyone else is sent on
  // to their store, or /app, by /get-app.
  { pattern: /^get-app$/, to: () => "/get-app" },
  { pattern: /^landing$/, to: () => "/" },
  // App resolves an episode to the channel/playlist holding it; the website
  // has a page per episode
  { pattern: /^video\/(.+)$/, to: (id) => `/video-details/${id}` },
  { pattern: /^channel\/(.+)$/, to: (id) => `/channel/${id}` },
  { pattern: /^playlist\/(.+)$/, to: (id) => `/playlist/${id}` },
  {
    pattern: /^giftcard(?:\/(.+))?$/,
    to: (code) => (code ? `/usemycode/${code}` : "/usemycode"),
  },
  { pattern: /^favourites$/, to: () => "/favourites" },
  { pattern: /^watch-history$/, to: () => "/watch-history" },
  { pattern: /^search$/, to: () => "/search" },
  { pattern: /^notifications$/, to: () => "/inbox" },
  { pattern: /^settings$/, to: () => "/myaccount" },
  { pattern: /^login-email$/, to: () => "/login" },
  { pattern: /^login-phone$/, to: () => "/login" },
  { pattern: /^register$/, to: () => "/try" },
  { pattern: /^support$/, to: () => "/help" },
  { pattern: /^purchase$/, to: () => "/buy" },
  // A YouTube video id or catalogue uuid; the page sorts out which (#590)
  {
    pattern: /^youtube(?:\/(.+))?$/,
    to: (id) => (id ? `/youtube/${id}` : "/youtube"),
  },
];

// The path after /link/, trimmed of leading and trailing slashes the same
// way the app trims it (NavigationRef.ts navigateFromDeepLink).
export const normaliseLinkPath = (path: string | undefined): string =>
  (path || "").replace(/^\/+|\/+$/g, "");

// Step 1 of the resolution order: the website page matching an app link
// path, or undefined when the table has no row for it.
export const matchLinkRoute = (path: string): string | undefined => {
  for (const route of LINK_ROUTES) {
    const match = path.match(route.pattern);
    if (match) {
      return route.to(match[1]);
    }
  }
  return undefined;
};

// Link arrivals are recorded for the acquisition funnel (#576) with
// utm_source=deeplink, unless the link already names its own source (for
// example ?utm_source=youtube). utm_source rather than a new key because
// the API keeps only the utm_* keys and campaign (TypeOrmProfileService
// ATTRIBUTION_TOUCH_KEYS). The result is forwarded to the destination page,
// so a campaign page that records the arrival again records the same source.
export const LINK_UTM_SOURCE = "deeplink";

export const withLinkSource = (search: string): string => {
  const params = new URLSearchParams(search);
  if (!params.get("utm_source")) {
    params.set("utm_source", LINK_UTM_SOURCE);
  }
  return `?${params.toString()}`;
};
